import {HubblTask, Palette, SimpleUser} from 'hubbl-shared';
import {memo, useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import styled, {keyframes} from 'styled-components';
import {
  AvatarStorageRef,
  DropdownItem,
  PrimaryButton,
  SecondaryButton,
  TextHeading6,
  WhiteContainer,
} from '../index';
import {useI18n} from '../../hooks';

interface HubblTaskItemProps {
  task: HubblTask;
  attendees: SimpleUser[];
  currentUserId: string;
  isUserCreator: boolean;
  onAssign?: (taskId: string, userId: string) => void;
  onUnassign?: (taskId: string) => void;
  onDelete: (taskId: string) => void;
  isLoading?: boolean;
  isCreationMode?: boolean;
  isUserAttending?: boolean;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const TaskContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
`;

const AssignmentIndicator = styled.div<{isAssigned: boolean}>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid
    ${props =>
      props.isAssigned ? Palette.primaryPadua : Palette.primaryTerraCotta};
  background: ${props =>
    props.isAssigned ? Palette.primaryPadua : 'transparent'};
  flex-shrink: 0;
`;

const TaskMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const AssignedUser = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: ${Palette.primaryPadua}40;
  border-radius: 12px;
  font-size: 12px;
  color: ${Palette.almostBlack};
  font-weight: 500;
`;

const UnassignedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: ${Palette.primaryCherokee}60;
  border-radius: 12px;
  font-size: 12px;
  color: ${Palette.almostBlack};
  font-weight: 500;
`;

const TaskActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${Palette.whiteTransparent60};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${Palette.almostBlackTransparent10};
  border-top: 2px solid ${Palette.primaryTerraCotta};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const DropdownWrapper = styled.div`
  position: relative;
`;

const PortalDropdownContainer = styled.div<{top: number; left: number}>`
  position: fixed;
  top: ${props => props.top}px;
  left: ${props => props.left}px;
  background: ${Palette.white};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  min-width: 160px;
  z-index: 9999;
  text-align: left;
  animation: ${fadeIn} 0.2s ease-out;
  transform-origin: bottom;
`;

const AssignButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const HubblTaskItem = memo(
  ({
    task,
    attendees,
    currentUserId,
    isUserCreator,
    onAssign,
    onUnassign,
    onDelete,
    isLoading = false,
    isCreationMode = false,
    isUserAttending = true,
  }: HubblTaskItemProps) => {
    const [showAssignDropdown, setShowAssignDropdown] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({top: 0, left: 0});
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);
    const t = useI18n();

    // Calculate dropdown position
    useEffect(() => {
      if (showAssignDropdown && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.top - 8, // 8px margin above the button
          left: rect.right - 160, // Align to right edge, 160px is min-width
        });
      }
    }, [showAssignDropdown]);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target as Node)
        ) {
          setShowAssignDropdown(false);
        }
      };

      if (showAssignDropdown) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [showAssignDropdown]);

    const isAssigned = task.assignedUserId !== null;
    const isAssignedToCurrentUser = task.assignedUserId === currentUserId;

    const handleAssign = useCallback(
      (userId: string) => {
        onAssign?.(task.id, userId);
        setShowAssignDropdown(false);
      },
      [onAssign, task.id],
    );

    const handleAssignMyself = useCallback(() => {
      onAssign?.(task.id, currentUserId);
    }, [onAssign, task.id, currentUserId]);

    const handleUnassign = useCallback(() => {
      onUnassign?.(task.id);
    }, [onUnassign, task.id]);

    const handleDelete = useCallback(() => {
      onDelete(task.id);
    }, [onDelete, task.id]);

    const availableUsers = attendees.filter(
      user => user.id !== task.assignedUserId && user.id !== currentUserId,
    );

    const assignedUser = attendees.find(
      user => user.id === task.assignedUserId,
    );

    return (
      <WhiteContainer
        color={isAssigned ? Palette.primarySubtleGreen : Palette.white}
        contentPadding={16}
        borderRadius={12}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          <TaskContent>
            <AssignmentIndicator isAssigned={isAssigned && !isCreationMode} />
            <TaskMeta>
              {isCreationMode ? (
                <UnassignedBadge>
                  ⏳ {t.events.willBeAssignedLater}
                </UnassignedBadge>
              ) : isAssigned ? (
                <AssignedUser>
                  <AvatarStorageRef
                    imageRef={assignedUser?.photoUrl}
                    name={assignedUser?.firstName || 'Unknown'}
                    size={20}
                  />
                  {isAssignedToCurrentUser
                    ? t.activities.you
                    : assignedUser?.firstName || 'Unknown'}
                </AssignedUser>
              ) : (
                <UnassignedBadge>⏳ {t.events.unassigned}</UnassignedBadge>
              )}
            </TaskMeta>
            <TextHeading6 text={task.title} />
          </TaskContent>

          <TaskActions>
            {isLoading && (
              <LoadingOverlay>
                <LoadingSpinner />
              </LoadingOverlay>
            )}
            {/* Only show assignment UI if not in creation mode */}
            {!isCreationMode && (
              <>
                {isAssigned ? (
                  isAssignedToCurrentUser ? (
                    <SecondaryButton
                      label={t.events.unassign}
                      onClick={handleUnassign}
                      isDisabled={isLoading}
                      isCompact={true}
                      customWidth={100}
                    />
                  ) : isUserCreator ? (
                    <SecondaryButton
                      label={t.events.unassign}
                      onClick={handleUnassign}
                      isDisabled={isLoading}
                      isCompact={true}
                      customWidth={100}
                    />
                  ) : null
                ) : (
                  <AssignButtonGroup>
                    {isUserAttending && (
                      <PrimaryButton
                        label={t.events.assignMyself}
                        onClick={handleAssignMyself}
                        isDisabled={isLoading}
                        isCompact={true}
                        customWidth={120}
                      />
                    )}
                    {isUserCreator && availableUsers.length > 0 && (
                      <DropdownWrapper>
                        <div ref={buttonRef}>
                          <SecondaryButton
                            label={t.events.assignOthers}
                            onClick={() =>
                              setShowAssignDropdown(!showAssignDropdown)
                            }
                            isDisabled={isLoading}
                            isCompact={true}
                            customWidth={120}
                          />
                        </div>
                        {showAssignDropdown &&
                          createPortal(
                            <PortalDropdownContainer
                              ref={dropdownRef}
                              top={dropdownPosition.top}
                              left={dropdownPosition.left}>
                              {availableUsers.map(user => (
                                <DropdownItem
                                  key={user.id}
                                  onClick={() => handleAssign(user.id)}>
                                  <AvatarStorageRef
                                    imageRef={user.photoUrl}
                                    name={user.firstName || 'Unknown'}
                                    size={20}
                                  />
                                  <TextHeading6
                                    text={user.firstName || 'Unknown'}
                                  />
                                </DropdownItem>
                              ))}
                            </PortalDropdownContainer>,
                            document.body,
                          )}
                      </DropdownWrapper>
                    )}
                  </AssignButtonGroup>
                )}
              </>
            )}

            {isUserCreator && (
              <SecondaryButton
                label={t.common.delete}
                onClick={handleDelete}
                isDisabled={isLoading}
                isCompact={true}
                customColor={Palette.primaryTerraCotta}
                customWidth={100}
              />
            )}
          </TaskActions>
        </div>
      </WhiteContainer>
    );
  },
);
